import {
  AnomalyChart,
  DropDown,
  Flex,
  FlexColumn,
  FormControls,
  LoadingButton,
  TextField,
  ToggleButton,
} from '@elementary/common-ui/components';
import {
  AnomalyDetectionChart,
  AutomatedTestAnomalyConfig,
} from '@elementary/common-ui/types';
import { ToggleButtonGroup, Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { anomalyDirectionOptions } from 'shared/constants';
import TimeWithUnitField from 'shared/components/TimeWithUnitField';
import {
  pipelineThresholdUnitOptions,
  sensitivityOptions,
  trainingPeriodOptions,
} from 'shared/constants';
import TrainingPeriodInfoTooltip from 'shared/components/TrainingPeriodInfoTooltip';
import { PipelineTaskPerformanceTestConfig } from '../../types';

type Props = {
  methods: UseFormReturn<PipelineTaskPerformanceTestConfig>;
  readOnly?: boolean;
  onSimulate: (config: AutomatedTestAnomalyConfig) => void;
  isSimulating: boolean;
  chartData: AnomalyDetectionChart | null;
};

const PipelineTaskPerformanceTestConfigurationFields = ({
  methods,
  readOnly,
  onSimulate,
  isSimulating,
  chartData,
}: Props) => {
  const mode = methods.watch('mode');

  const handleSimulate = () => {
    const { sensitivity, anomaly_direction, training_period } =
      methods.getValues();
    onSimulate({
      sensitivity: sensitivity ?? 'medium',
      anomaly_direction: anomaly_direction ?? 'BOTH',
      training_period: training_period ?? null,
    } as AutomatedTestAnomalyConfig);
  };

  return (
    <>
      <FormControls.FieldsWrapper>
        <FlexColumn sx={{ gap: 2 }}>
          <FormControls.FormTitle>Detection mode</FormControls.FormTitle>
          <ToggleButtonGroup
            exclusive
            value={mode}
            disabled={readOnly}
            onChange={(_, val) => {
              if (!val) return;
              methods.reset({
                ...methods.getValues(),
                mode: val as 'anomaly' | 'threshold',
              });
            }}
          >
            <ToggleButton value="anomaly" size="large" sx={{ width: '300px' }}>
              Anomaly detection
            </ToggleButton>
            <ToggleButton
              value="threshold"
              size="large"
              sx={{ width: '300px' }}
            >
              Static threshold
            </ToggleButton>
          </ToggleButtonGroup>
        </FlexColumn>
      </FormControls.FieldsWrapper>

      <FlexColumn
        sx={{ gap: 3, display: mode === 'anomaly' ? 'flex' : 'none' }}
      >
        <FormControls.FieldsWrapper style={{ gap: '8px' }}>
          <FormControls.FormTitle>Anomaly direction</FormControls.FormTitle>
          <FormControls.Row style={{ gap: '8px', flexWrap: 'wrap' }}>
            <FormControls.FormText>Detect</FormControls.FormText>
            <Controller
              control={methods.control}
              name="anomaly_direction"
              render={({ field }) => (
                <DropDown
                  sx={{ minWidth: '160px' }}
                  sortOptions={false}
                  options={anomalyDirectionOptions}
                  {...field}
                  disabled={readOnly || field.disabled}
                />
              )}
            />
            <FormControls.FormText>
              {`in the task's run duration.`}
            </FormControls.FormText>
          </FormControls.Row>
        </FormControls.FieldsWrapper>

        <FormControls.FieldsWrapper style={{ gap: '8px' }}>
          <FormControls.FormTitle>Sensitivity</FormControls.FormTitle>
          <FormControls.Row style={{ gap: '8px', flexWrap: 'wrap' }}>
            <FormControls.FormText>
              Adjust test thresholds by setting the sensitivity level to be
            </FormControls.FormText>
            <Controller
              control={methods.control}
              name="sensitivity"
              render={({ field }) => (
                <DropDown
                  sortOptions={false}
                  options={sensitivityOptions}
                  {...field}
                  disabled={readOnly || field.disabled}
                />
              )}
            />
          </FormControls.Row>
        </FormControls.FieldsWrapper>
      </FlexColumn>

      {mode === 'threshold' && (
        <FormControls.FieldsWrapper style={{ gap: '8px' }}>
          <FormControls.FormTitle>Threshold</FormControls.FormTitle>
          <Flex sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControls.FormText>
              Alert when run duration exceeds
            </FormControls.FormText>
            <Controller
              control={methods.control}
              name="fixed_threshold"
              render={({ field }) => (
                <TimeWithUnitField
                  disabled={!!readOnly}
                  onChange={field.onChange}
                  count={methods.watch('fixed_threshold.count')}
                  period={methods.watch('fixed_threshold.period')}
                  options={pipelineThresholdUnitOptions}
                  defaultPeriod="hour"
                />
              )}
            />
          </Flex>
        </FormControls.FieldsWrapper>
      )}

      <FormControls.FieldsWrapper style={{ gap: '8px' }}>
        <Flex sx={{ alignItems: 'center', gap: '4px' }}>
          <FormControls.FormTitle>Training period</FormControls.FormTitle>
          <TrainingPeriodInfoTooltip />
        </Flex>
        <FormControls.Row
          style={{ gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}
        >
          <Typography component="span">
            Base the model on the previous
          </Typography>
          <TextField
            sx={{ width: '70px', minWidth: '70px' }}
            type="number"
            disabled={readOnly}
            {...methods.register('training_period.count', {
              valueAsNumber: true,
            })}
          />
          <Controller
            control={methods.control}
            name="training_period.period"
            render={({ field }) => (
              <DropDown
                sortOptions={false}
                options={trainingPeriodOptions}
                sx={{ minWidth: '120px' }}
                {...field}
                disabled={readOnly || field.disabled}
              />
            )}
          />
        </FormControls.Row>
      </FormControls.FieldsWrapper>

      <FormControls.FieldsWrapper>
        {!readOnly && (
          <LoadingButton
            sx={{ width: 'fit-content' }}
            variant="outlined"
            loading={isSimulating}
            onClick={handleSimulate}
          >
            Simulate configuration
          </LoadingButton>
        )}

        {chartData && (
          <AnomalyChart
            metrics={chartData.metrics}
            status={chartData.status}
            metricsTitle={chartData.metricsTitle}
            anomalyDirection={chartData.anomalyDirection}
            detectionPeriod={chartData.detectionPeriod}
            detectionDelay={chartData.detectionDelay}
          />
        )}
      </FormControls.FieldsWrapper>
    </>
  );
};

export default PipelineTaskPerformanceTestConfigurationFields;
